import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    contentChild,
    DestroyRef,
    ElementRef,
    inject,
    input,
    OnInit,
    TemplateRef,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { VISIBILITY_OPTIONS, VisibilityService } from '@df/ui/common/visibility.service';
import { animationFrameScheduler, asapScheduler } from 'rxjs';
import { auditTime } from 'rxjs/operators';

/**
 * Scheduler to be used for scroll events. Needs to fall back to
 * something that doesn't rely on requestAnimationFrame on environments
 * that don't support it (e.g. server-side rendering).
 */
const SCROLL_SCHEDULER = typeof requestAnimationFrame !== 'undefined' ? animationFrameScheduler : asapScheduler;

@Component({
    selector: 'skeleton-wrapper,[skeletonWrapper]',
    template: `
        <ng-container [ngTemplateOutlet]="outputRef" />
        <ng-template #sourceRef><ng-content /></ng-template>
    `,
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SkeletonWrapperComponent implements OnInit, AfterViewInit {
    index = input.required<number>({ alias: 'skeletonWrapper' });
    threshold = input<number | number[]>(VISIBILITY_OPTIONS.threshold, { alias: 'skeletonWrapperThreshold' });
    rootMargin = input<string>(VISIBILITY_OPTIONS.rootMargin, { alias: 'skeletonWrapperRootMargin' });

    // placeholderRef is the template to display when the content is not loaded
    placeholderRef = contentChild<string, TemplateRef<unknown>>('skeletonPlaceholderRef', { read: TemplateRef<unknown> });
    placeholderRef$ = toObservable(this.placeholderRef);

    // sourceRef is the template to display when the content is loaded
    sourceRef = viewChild<string, TemplateRef<unknown>>('sourceRef', { read: TemplateRef<unknown> });
    sourceRef$ = toObservable(this.sourceRef);

    // outputRef is the template to display
    outputRef: TemplateRef<unknown> | null = null;

    protected visibilityService = inject(VisibilityService);
    protected elementRef = inject(ElementRef);
    protected destroyRef = inject(DestroyRef);
    protected changeDetectorRef = inject(ChangeDetectorRef);

    private _loaded = false;

    ngOnInit() {
        this.outputRef = this.placeholderRef() ?? null;
    }

    ngAfterViewInit() {
        this.visibilityService
            .isInView(this.elementRef.nativeElement, {
                threshold: this.threshold(),
                rootMargin: this.rootMargin()
            })
            .pipe(takeUntilDestroyed(this.destroyRef), auditTime(0, SCROLL_SCHEDULER))
            .subscribe(visible => {
                if (visible && !this._loaded) {
                    this._loaded = true;

                    this.outputRef = this.sourceRef() ?? null;

                    this.changeDetectorRef.markForCheck();
                }
            });
    }
}
